import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FolderKanban, Loader2, Users, UserPlus, X, KeyRound, Copy, Crown, Eye, EyeOff, FileArchive, RotateCw, BarChart3, Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GenerationCard from "@/components/GenerationCard";

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [project, setProject] = useState(null);
  const [role, setRole] = useState("member");
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [varLoading, setVarLoading] = useState(null);
  const [team, setTeam] = useState(null);
  const [usage, setUsage] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const timer = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/generations`);
      setProject(data.project);
      setRole(data.role);
      setGenerations(data.generations);
    } catch {
      navigate("/dashboard/projects", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  const fetchTeam = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/members`); setTeam(data); } catch { /* noop */ }
    try { const { data } = await api.get(`/projects/${projectId}/usage`); setUsage(data); } catch { setUsage(null); }
  }, [projectId]);

  useEffect(() => { fetchData(); fetchTeam(); }, [fetchData, fetchTeam]);

  useEffect(() => {
    if (generations.some((g) => g.status === "processing")) timer.current = setTimeout(fetchData, 3000);
    return () => clearTimeout(timer.current);
  }, [generations, fetchData]);

  const onVariation = async (id) => {
    setVarLoading(id);
    try { await api.post(`/generations/${id}/variation`); toast.success("Variation started."); await fetchData(); await refreshUser(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setVarLoading(null); }
  };
  const onDelete = async (id) => {
    try { await api.delete(`/generations/${id}`); await fetchData(); }
    catch { toast.error("Only the track owner can delete it."); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post(`/projects/${projectId}/invite`, { email: inviteEmail.trim() });
      setInviteEmail("");
      await fetchTeam();
      toast.success("Teammate added to the project.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setInviting(false); }
  };

  const removeMember = async (uid) => {
    try { await api.delete(`/projects/${projectId}/members/${uid}`); await fetchTeam(); toast.success("Member removed."); }
    catch { toast.error("Could not remove member."); }
  };

  const copyKey = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("API key copied."); }
    catch { toast.error("Copy failed."); }
  };

  const isOwner = role === "owner";
  const [revealedKeys, setRevealedKeys] = useState({});
  const [zipping, setZipping] = useState(false);

  const revealKey = async (keyId) => {
    if (revealedKeys[keyId]) { setRevealedKeys((r) => { const n = { ...r }; delete n[keyId]; return n; }); return; }
    try {
      const { data } = await api.get(`/projects/${projectId}/api-keys/${keyId}/reveal`);
      setRevealedKeys((r) => ({ ...r, [keyId]: data.key }));
    } catch { toast.error("Only the owner can reveal keys."); }
  };
  const copyKeyById = async (keyId) => {
    let full = revealedKeys[keyId];
    if (!full) {
      try { const { data } = await api.get(`/projects/${projectId}/api-keys/${keyId}/reveal`); full = data.key; }
      catch { toast.error("Only the owner can copy the full key."); return; }
    }
    copyKey(full);
  };

  const rotateKey = async (keyId) => {
    try {
      await api.post(`/api-keys/${keyId}/rotate`);
      setRevealedKeys((r) => { const n = { ...r }; delete n[keyId]; return n; });
      await fetchTeam();
      toast.success("Key rotated — updated for the whole team.");
    } catch { toast.error("Only the owner can rotate keys."); }
  };

  const exportUsageCsv = () => {
    if (!usage || !usage.rows?.length) return;
    const header = ["Name", "Email", "Role", "Tracks", "Credits"];
    const body = usage.rows.map((r) => [r.name, r.email, r.is_owner ? "owner" : "member", r.count, r.credits]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header, ...body].map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(project?.name || "project").replace(/\s+/g, "-")}-usage.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Usage exported as CSV.");
  };

  const downloadProjectZip = async () => {    setZipping(true);
    const tid = toast.loading("Preparing your soundtrack…");
    const filename = `${(project?.name || "project").replace(/\s+/g, "-")}-soundtrack.zip`;
    try {
      const res = await fetch(`${API}/projects/${projectId}/download-zip`, { credentials: "include" });
      if (!res.ok) throw new Error("zip failed");

      // Preferred: stream straight to disk via File System Access API (no full in-memory blob)
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({ suggestedName: filename, types: [{ description: "Zip", accept: { "application/zip": [".zip"] } }] });
          const writable = await handle.createWritable();
          const reader = res.body.getReader();
          let received = 0;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            await writable.write(value);
            received += value.length;
            toast.loading(`Saving soundtrack… ${(received / 1048576).toFixed(1)} MB`, { id: tid });
          }
          await writable.close();
          toast.success(`Saved ${(received / 1048576).toFixed(1)} MB to disk`, { id: tid });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") { toast.dismiss(tid); return; }
          // fall through to blob fallback
        }
      }

      // Fallback: buffer then save
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        toast.loading(`Downloading soundtrack… ${(received / 1048576).toFixed(1)} MB`, { id: tid });
      }
      const blob = new Blob(chunks, { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(received / 1048576).toFixed(1)} MB`, { id: tid });
    } catch {
      toast.error("Could not download the project soundtrack.", { id: tid });
    } finally { setZipping(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <Button variant="ghost" onClick={() => navigate("/dashboard/projects")} className="text-muted-foreground mb-4 -ml-2" data-testid="back-to-projects">
        <ArrowLeft size={16} className="mr-1.5" /> Projects
      </Button>
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <FolderKanban className="text-primary" /> {project?.name || "Project"}
            {!isOwner && <Badge className="bg-accent/20 text-accent border-accent/30">Shared with you</Badge>}
          </h1>
          {project?.description && <p className="text-muted-foreground mt-1">{project.description}</p>}
        </div>

        <div className="flex items-center gap-2">
          {generations.some((g) => g.status === "completed") && (
            <Button onClick={downloadProjectZip} disabled={zipping} variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 gap-2" data-testid="project-download-zip">
              {zipping ? <Loader2 className="animate-spin" size={16} /> : <FileArchive size={16} />} Download all
            </Button>
          )}
          <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-border gap-2" data-testid="open-team">
              <Users size={16} className="text-primary" /> Team {team ? `(${1 + (team.members?.length || 0)})` : ""}
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Users className="text-primary" size={20} /> Project Team</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Manage teammates and shared API keys for this project.</DialogDescription>
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">Teammates share this project&apos;s tracks and generate into it using the owner&apos;s credit pool.</p>

              {isOwner && (
                <div className="flex gap-2">
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@studio.com" className="bg-background border-border" data-testid="invite-email-input" />
                  <Button onClick={invite} disabled={inviting} className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0" data-testid="invite-submit">
                    {inviting ? <Loader2 className="animate-spin" size={16} /> : <><UserPlus size={15} className="mr-1" /> Invite</>}
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                {team?.owner && (
                  <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">{team.owner.name?.[0] || "O"}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{team.owner.name}</div><div className="text-xs text-muted-foreground truncate">{team.owner.email}</div></div>
                    <Badge className="bg-primary/15 text-primary border-primary/30 gap-1"><Crown size={11} /> Owner</Badge>
                  </div>
                )}
                {(team?.members || []).map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`member-${m.user_id}`}>
                    <div className="w-8 h-8 rounded-full bg-accent/30 flex items-center justify-center text-xs font-bold">{m.name?.[0] || "M"}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.name}</div><div className="text-xs text-muted-foreground truncate">{m.email}</div></div>
                    {isOwner && <Button variant="ghost" size="icon" onClick={() => removeMember(m.user_id)} className="text-muted-foreground hover:text-destructive h-8 w-8" data-testid={`remove-member-${m.user_id}`}><X size={15} /></Button>}
                  </div>
                ))}
                {team && !team.members?.length && isOwner && <div className="text-xs text-muted-foreground">No teammates yet — invite one above.</div>}
              </div>

              {isOwner && usage && usage.rows.length > 0 && (
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground"><BarChart3 size={13} /> CREDIT USAGE</div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground">{usage.total_credits} credits · {usage.total_tracks} tracks</div>
                      <Button variant="ghost" size="sm" onClick={exportUsageCsv} className="h-7 text-muted-foreground hover:text-primary gap-1 px-2" data-testid="export-usage-csv" title="Export usage as CSV">
                        <Download size={13} /> CSV
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1.5" data-testid="usage-panel">
                    {usage.rows.map((r) => (
                      <div key={r.user_id} className="flex items-center gap-2 text-sm" data-testid={`usage-row-${r.user_id}`}>
                        <span className="flex-1 truncate">{r.name}{r.is_owner && <span className="text-[10px] text-primary ml-1">(owner)</span>}</span>
                        <span className="text-muted-foreground text-xs">{r.count} tracks</span>
                        <span className="font-mono font-bold w-12 text-right">{r.credits}</span>
                        <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${usage.total_credits ? (r.credits / usage.total_credits) * 100 : 0}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {usage.monthly?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-border/60" data-testid="usage-monthly-chart">
                      <div className="text-[10px] font-mono text-muted-foreground mb-2">MONTHLY CREDIT TREND</div>
                      <div className="flex items-end gap-1.5 h-20">
                        {(() => {
                          const max = Math.max(1, ...usage.monthly.map((m) => m.credits));
                          return usage.monthly.map((m) => (
                            <div key={m.month} className="flex-1 h-full flex flex-col items-center justify-end gap-1" data-testid={`month-bar-${m.month}`} title={`${m.month}: ${m.credits} credits`}>
                              <span className="text-[9px] font-mono text-muted-foreground h-3 leading-3">{m.credits > 0 ? m.credits : ""}</span>
                              <div className={`w-full rounded-t transition-all ${m.credits > 0 ? "bg-gradient-to-t from-primary/40 to-primary" : "bg-muted"}`} style={{ height: `${Math.max(2, (m.credits / max) * 46)}px` }} />
                              <span className="text-[9px] font-mono text-muted-foreground/70 leading-3">{m.month.slice(5)}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {team?.shared_api_keys?.length > 0 && (
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-2"><KeyRound size={13} /> SHARED API KEYS</div>
                  <div className="space-y-2">
                    {team.shared_api_keys.map((k) => (
                      <div key={k.id} className="flex items-center gap-2 rounded-lg border border-border bg-background p-2" data-testid={`shared-key-${k.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium">{k.name}</div>
                          <div className="font-mono text-[11px] text-muted-foreground truncate" data-testid={`key-value-${k.id}`}>{revealedKeys[k.id] || k.masked}</div>
                          <div className="text-[10px] text-muted-foreground/70 mt-0.5" data-testid={`key-rotated-${k.id}`}>
                            {k.rotated_at ? `Rotated ${new Date(k.rotated_at).toLocaleDateString()} by ${k.rotated_by || "owner"}` : "Never rotated"}
                          </div>
                        </div>
                        {isOwner && (
                          <Button variant="ghost" size="icon" onClick={() => revealKey(k.id)} className="text-muted-foreground hover:text-primary h-7 w-7" data-testid={`reveal-key-${k.id}`} title={revealedKeys[k.id] ? "Hide" : "Reveal"}>
                            {revealedKeys[k.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </Button>
                        )}
                        {isOwner && (
                          <Button variant="ghost" size="icon" onClick={() => rotateKey(k.id)} className="text-muted-foreground hover:text-primary h-7 w-7" data-testid={`rotate-key-${k.id}`} title="Rotate key"><RotateCw size={14} /></Button>
                        )}
                        {isOwner && (
                          <Button variant="ghost" size="icon" onClick={() => copyKeyById(k.id)} className="text-muted-foreground hover:text-primary h-7 w-7" data-testid={`copy-key-${k.id}`}><Copy size={14} /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {isOwner && user && user.credits < 500 && (
        <div data-testid="low-credit-alert" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200">
          <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold">Low shared credit pool.</span> Only <span className="font-mono font-bold">{user.credits}</span> credits left. Teammates generating into this project draw from your pool —{" "}
            <button onClick={() => navigate("/dashboard/billing")} className="underline hover:text-amber-100 font-medium" data-testid="low-credit-topup">top up now</button> to avoid interruptions.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin text-primary" size={18} /> Loading…</div>
      ) : generations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-project-generations">
          No tracks in this project yet. In the Studio, assign a generation to this project.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {generations.map((g) => (
            <GenerationCard key={g.id} gen={g} onVariation={onVariation} onDelete={onDelete} varLoading={varLoading === g.id} canModify={g.user_id === user?.user_id} onSaved={fetchData} />
          ))}
        </div>
      )}
    </div>
  );
}
